import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import {
  db,
  auth,
  setupAdminPresence,
  cleanupAdminPresence,
  setPersistentAuth,
} from "../../firebase";
import { signInWithEmailAndPassword, updateProfile } from "firebase/auth";
import {
  ref,
  push,
  remove,
  onValue,
  set,
  get,
  update,
  serverTimestamp,
} from "firebase/database";
import {
  FiTrash2,
  FiCheckSquare,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiArrowLeft,
} from "react-icons/fi";
import Profile from "./AdminProfile";
import Navbar from "./Navbar";

const Message = React.memo(
  ({ msg, isLastAdminMsg, isLastReadAdminMsg, isMobileView = false }) => {
    return (
      <div
        className={`${isMobileView ? "mb-4" : "mb-6"} flex ${
          msg.sender === "admin" ? "justify-end" : "justify-start"
        }`}
      >
        <div className="flex flex-col items-start max-w-[85%] md:max-w-md">
          <div
            className={`p-${isMobileView ? "2 sm:p-3" : "4"} rounded-2xl break-words shadow-md transition-all duration-200 ${
              msg.sender === "admin"
                ? "bg-linear-to-r from-indigo-500 to-blue-600 text-white"
                : "bg-gray-700 text-white"
            }`}
          >
            <p className="text-base font-medium whitespace-pre-line">
              {msg.text}
            </p>
          </div>
          <div
            className={`mt-1 text-xs text-gray-400 ${msg.sender === "admin" ? "self-end" : "self-start"}`}
          >
            {msg.sender === "admin" &&
              ((isLastAdminMsg && msg.status !== "read") ||
                (isLastReadAdminMsg && msg.status === "read")) && (
                <span className="mr-2">
                  {msg.status.charAt(0).toUpperCase() +
                    msg.status.slice(1)}{" "}
                </span>
              )}
            <span>
              {new Date(msg.timestamp).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })}
            </span>
          </div>
        </div>
      </div>
    );
  }
);

Message.displayName = "Message";

Message.propTypes = {
  msg: PropTypes.shape({
    sender: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    timestamp: PropTypes.number.isRequired,
  }).isRequired,
  isLastAdminMsg: PropTypes.bool.isRequired,
  isLastReadAdminMsg: PropTypes.bool.isRequired,
  isMobileView: PropTypes.bool,
};

const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [adminName, setAdminName] = useState("");
  const [showNameModal, setShowNameModal] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState("");
  const [showProfileOverlay, setShowProfileOverlay] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");

  const [chats, setChats] = useState({});
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [response, setResponse] = useState("");
  const [userTyping, setUserTyping] = useState(false);
  const [chatFilter, setChatFilter] = useState("Active");
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [showMultiDeleteConfirm, setShowMultiDeleteConfirm] = useState(false);
  const [selectedChats, setSelectedChats] = useState([]);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [chatToDelete, setChatToDelete] = useState(null);

  const messagesEndRef = useRef(null);

  const defaultTitle = "Admin Panel";
  const [isChatListCollapsed, setIsChatListCollapsed] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [showMobileChatView, setShowMobileChatView] = useState(false);

  const quickResponses = [
    "Hello! How can I help you today?",
    "Thank you for contacting us.",
    "I'll look into this right away.",
    "Could you please provide more details?",
  ];
  const adminTypingTimeoutRef = useRef(null);

  const isChatConnected =
    selectedChat && chats[selectedChat]?.connectedAdmin?.email === adminEmail;

  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth < 1025);
    };

    checkMobileView();

    window.addEventListener("resize", checkMobileView);
    return () => {
      window.removeEventListener("resize", checkMobileView);
    };
  }, []);

  useEffect(() => {
    if (isMobileView && selectedChat) {
      setShowMobileChatView(true);
    }
  }, [selectedChat, isMobileView]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setIsAuthenticated(true);
        setAdminEmail(user.email);

        if (user.displayName) {
          setAdminName(user.displayName);
        } else {
          setShowNameModal(true);
        }

        const pageSessionId = Date.now().toString();
        sessionStorage.setItem("pageSessionId", pageSessionId);

        setTimeout(() => {
          setupAdminPresence(user.uid);
        }, 500);
      }
    });

    window.addEventListener("storage", (event) => {
      if (event.key === "adminForcedLogout" && event.newValue) {
        window.location.reload();
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await setPersistentAuth();

      const userCredential = await signInWithEmailAndPassword(
        auth,
        credentials.email,
        credentials.password
      );

      setIsAuthenticated(true);
      const user = userCredential.user;
      setAdminEmail(user.email);

      if (!user.displayName) {
        setShowNameModal(true);
      } else {
        setAdminName(user.displayName);
      }

      setTimeout(() => {
        setupAdminPresence(user.uid);
      }, 500);
    } catch (error) {
      console.error("Login error:", error);
      alert("Invalid email or password.");
      setIsAuthenticated(false);
    }
  };

  const handleLogout = async () => {
    try {
      if (auth.currentUser) {
        const userId = auth.currentUser.uid;

        try {
          let connectionId;
          try {
            connectionId = sessionStorage.getItem("adminConnectionId");
          } catch (e) {
            console.warn("Could not access sessionStorage:", e);
          }

          if (connectionId) {
            const currentSessionRef = ref(
              db,
              `admins/${userId}/sessions/web/${connectionId}`
            );
            await remove(currentSessionRef);

            const webSessionsRef = ref(db, `admins/${userId}/sessions/web`);
            const mobileSessionsRef = ref(
              db,
              `admins/${userId}/sessions/mobile`
            );

            const [webSnapshot, mobileSnapshot] = await Promise.all([
              get(webSessionsRef),
              get(mobileSessionsRef),
            ]);

            const webSessions = webSnapshot.exists() ? webSnapshot.val() : {};
            const mobileSessions = mobileSnapshot.exists()
              ? mobileSnapshot.val()
              : {};

            const hasOtherSessions =
              Object.keys(webSessions).length > 0 ||
              Object.keys(mobileSessions).length > 0;

            if (!hasOtherSessions) {
              await update(ref(db, `admins/${userId}`), {
                globalStatus: "offline",
                lastUpdate: serverTimestamp(),
              });

              await update(ref(db, "globalPresence/adminIds"), {
                [userId]: null,
              });
            } else {
              const allSessions = { ...webSessions, ...mobileSessions };
              let bestStatus = "offline";

              Object.values(allSessions).forEach((session) => {
                if (session.status === "online") {
                  bestStatus = "online";
                } else if (
                  session.status === "away" &&
                  bestStatus === "offline"
                ) {
                  bestStatus = "away";
                }
              });

              await update(ref(db, `admins/${userId}`), {
                globalStatus: bestStatus,
                lastUpdate: serverTimestamp(),
              });

              if (bestStatus !== "offline") {
                await update(ref(db, "globalPresence/adminIds"), {
                  [userId]: true,
                });
              } else {
                await update(ref(db, "globalPresence/adminIds"), {
                  [userId]: null,
                });
              }
            }

            const adminsRef = ref(db, "globalPresence/adminIds");
            const adminsSnapshot = await get(adminsRef);

            const anyAdminsOnline =
              adminsSnapshot.exists() &&
              Object.keys(adminsSnapshot.val() || {}).length > 0;
            await update(ref(db, "adminStatus"), { status: anyAdminsOnline });
          } else {
            await cleanupAdminPresence(userId);
          }
        } catch (err) {
          console.error("Error updating status before logout:", err);
          await cleanupAdminPresence(userId);
        }
      }

      setIsAuthenticated(false);
      setCredentials({ email: "", password: "" });
      setSelectedChat(null);
      setChatMessages([]);

      await auth.signOut();

      try {
        sessionStorage.removeItem("adminConnectionId");
      } catch (e) {
        console.warn("Could not clear connectionId from sessionStorage:", e);
      }

      if (typeof window !== "undefined") {
        window.adminConnectionId = null;
      }

      window.location.href = "/admin";
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleSetDisplayName = async () => {
    if (!newDisplayName.trim()) return;
    try {
      await updateProfile(auth.currentUser, {
        displayName: newDisplayName.trim(),
      });
      setAdminName(newDisplayName.trim());
      setShowNameModal(false);
      if (
        selectedChat &&
        chats[selectedChat]?.connectedAdmin?.email === adminEmail
      ) {
        update(ref(db, `chats/${selectedChat}`), {
          connectedAdmin: { email: adminEmail, name: newDisplayName.trim() },
        });
      }
    } catch (error) {
      console.error("Error updating display name:", error);
      alert("Failed to update name.");
    }
  };

  const renderNameModal = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-900 p-6 sm:p-8 rounded-xl shadow-2xl transform transition-all w-[90%] sm:max-w-lg sm:w-full border border-gray-700">
        <div className="mb-6 sm:mb-8 text-center">
          <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">
            Set Your Display Name
          </h3>
          <p className="text-gray-400 text-sm">
            This name will be visible to users when you chat with them
          </p>
        </div>

        <div className="space-y-6">
          <div className="relative">
            <input
              type="text"
              value={newDisplayName}
              onChange={(e) => setNewDisplayName(e.target.value.slice(0, 50))}
              placeholder="Enter your name"
              maxLength={50}
              className="w-full p-4 rounded-lg bg-gray-800 text-white placeholder-gray-400 
                     border border-gray-700 focus:outline-hidden focus:ring-2 focus:ring-blue-500 
                     focus:border-transparent transition-all duration-200"
            />
            <span
              className={`absolute right-4 top-4 text-sm ${
                newDisplayName.length >= 50 ? "text-red-400" : "text-gray-400"
              }`}
            >
              {newDisplayName.length}/50
            </span>
          </div>

          <button
            onClick={handleSetDisplayName}
            disabled={!newDisplayName.trim()}
            className="w-full py-4 bg-linear-to-r from-blue-600 to-purple-600 
                   text-white rounded-lg font-semibold transform transition-all 
                   duration-200 hover:from-blue-700 hover:to-purple-700 
                   focus:outline-hidden focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 
                   focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save Name
          </button>
        </div>
      </div>
    </div>
  );
  const markMessagesAsRead = useCallback(
    (chatId, messages) => {
      if (!chats[chatId]?.connectedAdmin?.email === adminEmail) return;

      const updates = {};
      let hasUpdates = false;

      messages.forEach((msg) => {
        if (
          msg.id &&
          msg.sender === "user" &&
          msg.text &&
          msg.status !== "read"
        ) {
          updates[`chats/${chatId}/messages/${msg.id}/status`] = "read";
          hasUpdates = true;
        }
      });

      if (hasUpdates) {
        update(ref(db), updates).catch(console.error);
      }
    },
    [adminEmail, chats]
  );

  const handleConnectChat = useCallback(() => {
    if (selectedChat && adminEmail) {
      update(ref(db, `chats/${selectedChat}`), {
        connectedAdmin: { email: adminEmail, name: adminName },
      }).then(() => {
        markMessagesAsRead(selectedChat, chatMessages);
      });
    }
  }, [selectedChat, adminEmail, adminName, chatMessages, markMessagesAsRead]);
  useEffect(() => {
    if (!isAuthenticated) return;

    const chatsRef = ref(db, "chats");
    const unsubscribe = onValue(chatsRef, (snapshot) => {
      const data = snapshot.val() || {};
      // Use a more efficient comparison to avoid unnecessary updates
      setChats((prevChats) => {
        // Only update if there's an actual change in the structure
        const prevKeys = Object.keys(prevChats).sort();
        const newKeys = Object.keys(data).sort();

        if (
          prevKeys.length !== newKeys.length ||
          !prevKeys.every((key, index) => key === newKeys[index])
        ) {
          return data;
        }

        // Check if any chat has significant changes (not just message status updates)
        let hasSignificantChange = false;
        for (const chatId in data) {
          const prevChat = prevChats[chatId];
          const newChat = data[chatId];

          if (
            !prevChat ||
            prevChat.ended !== newChat.ended ||
            prevChat.connectedAdmin?.email !== newChat.connectedAdmin?.email ||
            Object.keys(prevChat.messages || {}).length !==
              Object.keys(newChat.messages || {}).length
          ) {
            hasSignificantChange = true;
            break;
          }
        }

        return hasSignificantChange ? data : prevChats;
      });
    });

    return () => unsubscribe();
  }, [isAuthenticated]);
  useEffect(() => {
    if (!selectedChat) {
      setChatMessages([]);
      return;
    }

    // Clear messages immediately when chat changes
    setChatMessages([]);

    const messagesRef = ref(db, `chats/${selectedChat}/messages`);
    const unsubscribe = onValue(messagesRef, (snapshot) => {
      const data = snapshot.val() || {};
      const messagesList = Object.entries(data).map(([id, message]) => ({
        id,
        ...message,
      }));
      messagesList.sort((a, b) => a.timestamp - b.timestamp);

      // Only update state if messages have actually changed
      setChatMessages((prevMessages) => {
        if (prevMessages.length !== messagesList.length) {
          return messagesList;
        }

        // Check if any message content or status has changed
        const hasChanges = messagesList.some((msg, index) => {
          const prevMsg = prevMessages[index];
          return (
            !prevMsg ||
            prevMsg.text !== msg.text ||
            prevMsg.status !== msg.status ||
            prevMsg.timestamp !== msg.timestamp
          );
        });

        return hasChanges ? messagesList : prevMessages;
      });

      // Mark messages as read after a short delay to allow for smooth rendering
      if (
        selectedChat === messagesRef.parent.key &&
        document.visibilityState === "visible"
      ) {
        setTimeout(() => {
          markMessagesAsRead(selectedChat, messagesList);
        }, 100);
      }
    });

    return () => unsubscribe();
  }, [selectedChat, markMessagesAsRead]);
  useEffect(() => {
    if (!selectedChat) return;
    const typingRef = ref(db, `chats/${selectedChat}/typing`);
    const unsubscribe = onValue(typingRef, (snapshot) => {
      const typingData = snapshot.val() || {};
      setUserTyping(typingData.user === true);
    });
    return () => unsubscribe();
  }, [selectedChat]);
  const calculateTotalUnreadCount = useCallback(
    (chatsData) => {
      return Object.entries(chatsData).reduce((total, [_chatId, chatData]) => {
        if (
          (!chatData?.connectedAdmin ||
            chatData?.connectedAdmin?.email === adminEmail) &&
          !chatData?.ended
        ) {
          const chatUnread = chatData.messages
            ? Object.values(chatData.messages).filter(
                (msg) => msg.sender === "user" && msg.status !== "read"
              ).length
            : 0;
          return total + chatUnread;
        }
        return 0;
      }, 0);
    },
    [adminEmail]
  );
  useEffect(() => {
    const updateTitle = () => {
      if (
        document.visibilityState === "visible" &&
        selectedChat &&
        isChatConnected
      ) {
        markMessagesAsRead(selectedChat, chatMessages);
        document.title = defaultTitle;
      } else {
        const totalUnread = calculateTotalUnreadCount(chats);
        if (totalUnread > 0) {
          document.title = `(${totalUnread}) New Message${totalUnread > 1 ? "s" : ""}`;
        } else {
          document.title = defaultTitle;
        }
      }
    };

    updateTitle();

    document.addEventListener("visibilitychange", updateTitle);
    return () => {
      document.removeEventListener("visibilitychange", updateTitle);
      document.title = defaultTitle;
    };
  }, [
    chatMessages,
    selectedChat,
    isChatConnected,
    defaultTitle,
    chats,
    adminEmail,
    calculateTotalUnreadCount,
    markMessagesAsRead,
  ]);
  const handleSendResponse = useCallback(async () => {
    if (!selectedChat || !response.trim() || !isChatConnected) return;

    // Check if chat is ended using both conditions
    const chatData = chats[selectedChat];
    if (chatData?.ended || chatData?.status?.state === "ended") return;

    const message = {
      text: response,
      sender: "admin",
      timestamp: Date.now(),
      status: "sent",
    };

    try {
      const newMessageRef = await push(
        ref(db, `chats/${selectedChat}/messages`),
        message
      );

      setResponse("");

      // Update status to delivered after a delay, but do it more smoothly
      setTimeout(() => {
        update(newMessageRef, { status: "delivered" }).catch(console.error);
      }, 1500); // Slightly reduced delay for better UX
    } catch (error) {
      console.error("Error sending message:", error);
    }
  }, [selectedChat, response, isChatConnected, chats]);

  const handleTyping = useCallback(() => {
    if (!selectedChat || !isChatConnected) return;
    const typingRef = ref(db, `chats/${selectedChat}/typing`);
    update(typingRef, { admin: true, user: false });
    if (adminTypingTimeoutRef.current)
      clearTimeout(adminTypingTimeoutRef.current);
    adminTypingTimeoutRef.current = setTimeout(() => {
      update(typingRef, { admin: false, user: false });
    }, 3000);
  }, [selectedChat, isChatConnected]);

  const markChatEnded = (chatId) => {
    set(ref(db, `chats/${chatId}/ended`), true);
    set(ref(db, `chats/${chatId}/status`), {
      state: "ended",
      updatedAt: serverTimestamp(),
      endedAt: serverTimestamp(),
    });
  };
  const handleChatSelect = useCallback(
    (chatId) => {
      if (selectedChat !== chatId) {
        setChatMessages([]);
        setSelectedChat(chatId);

        if (chats[chatId]?.connectedAdmin?.email === adminEmail) {
          const messages = Object.values(chats[chatId].messages || {});
          markMessagesAsRead(chatId, messages);
        }
      }
    },
    [selectedChat, chats, adminEmail, markMessagesAsRead]
  );

  const handleBackToList = () => {
    setShowMobileChatView(false);
    setSelectedChat(null);
  };

  const { lastAdminMsgId, lastReadAdminMsgId } = useMemo(() => {
    let lastId = null;
    let lastReadId = null;
    for (const msg of chatMessages) {
      if (msg.sender === "admin") {
        lastId = msg.id;
        if (msg.status === "read") {
          lastReadId = msg.id;
        }
      }
    }
    return { lastAdminMsgId: lastId, lastReadAdminMsgId: lastReadId };
  }, [chatMessages]);
  const filteredChats = useMemo(() => {
    return Object.entries(chats)
      .filter(([_chatId, chatData]) => {
        if (chatFilter === "Active")
          return !chatData.ended || chatData.status?.state === "active";
        if (chatFilter === "Connected")
          return (
            !chatData.ended && chatData.connectedAdmin?.email === adminEmail
          );
        if (chatFilter === "Ended")
          return chatData.ended || chatData.status?.state === "ended";
        if (chatFilter === "All") return true;
        return true;
      })
      .sort((a, b) => {
        const aMessages = Object.values(a[1].messages || {});
        const bMessages = Object.values(b[1].messages || {});
        const aLatestTimestamp = aMessages.length
          ? Math.max(...aMessages.map((msg) => msg.timestamp))
          : 0;
        const bLatestTimestamp = bMessages.length
          ? Math.max(...bMessages.map((msg) => msg.timestamp))
          : 0;
        return bLatestTimestamp - aLatestTimestamp;
      });
  }, [chats, chatFilter, adminEmail]);

  // Check both the ended flag and status.state for proper chat status determination
  const selectedChatStatus = useMemo(() => {
    if (!selectedChat || !chats[selectedChat]) return null;
    const chatData = chats[selectedChat];
    return chatData.ended || chatData.status?.state === "ended"
      ? "ended"
      : chatData.status?.state;
  }, [chats, selectedChat]);

  const handleDisplayNameUpdate = (newName) => {
    setAdminName(newName);
    Object.entries(chats).forEach(([chatId, chatData]) => {
      if (chatData?.connectedAdmin?.email === adminEmail) {
        update(ref(db, `chats/${chatId}/connectedAdmin`), {
          name: newName,
        }).catch((error) => {
          console.error(`Failed to update chat ${chatId}:`, error);
        });
      }
    });
  };

  const renderProfileOverlay = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black opacity-80 backdrop-blur-xs"></div>
      <div className="relative p-4 sm:p-8 rounded-xl shadow-2xl bg-gray-900/90 w-[95%] sm:w-full max-w-3xl">
        <button
          onClick={() => setShowProfileOverlay(false)}
          className="absolute top-4 right-4 text-white text-3xl hover:text-gray-300 transition"
        >
          &times;
        </button>
        <Profile onDisplayNameChange={handleDisplayNameUpdate} />
      </div>
    </div>
  );

  const handleDeleteClick = (chatId) => {
    const connectedAdmin = chats[chatId]?.connectedAdmin;
    if (connectedAdmin && connectedAdmin.email !== adminEmail) return;
    setChatToDelete(chatId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteChat = () => {
    if (chatToDelete) {
      remove(ref(db, `chats/${chatToDelete}`))
        .then(() => {
          setSelectedChat(null);
          setShowDeleteConfirm(false);
          setChatToDelete(null);
          if (isMobileView) {
            setShowMobileChatView(false);
          }
        })
        .catch((error) => {
          console.error("Error deleting chat:", error);
          alert("Failed to delete chat.");
        });
    }
  };

  const toggleChatSelection = (chatId) => {
    const connectedAdmin = chats[chatId]?.connectedAdmin;
    if (connectedAdmin && connectedAdmin.email !== adminEmail) return;

    setSelectedChats((prev) =>
      prev.includes(chatId)
        ? prev.filter((id) => id !== chatId)
        : [...prev, chatId]
    );
  };

  const deleteSelectedChats = () => {
    const deletableChats = selectedChats.filter((chatId) => {
      const connectedAdmin = chats[chatId]?.connectedAdmin;
      return !connectedAdmin || connectedAdmin.email === adminEmail;
    });

    if (deletableChats.length > 0) {
      setSelectedChats(deletableChats);
      setShowMultiDeleteConfirm(true);
    }
  };

  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  useEffect(() => {
    const calculateHeight = () => {
      const vh = window.innerHeight;
      setViewportHeight(vh);
    };

    calculateHeight();

    window.addEventListener("resize", calculateHeight);
    window.addEventListener("orientationchange", () => {
      setTimeout(calculateHeight, 100);
    });

    window.visualViewport?.addEventListener("resize", calculateHeight);

    return () => {
      window.removeEventListener("resize", calculateHeight);
      window.visualViewport?.removeEventListener("resize", calculateHeight);
    };
  }, []);

  const confirmDeleteSelectedChats = () => {
    if (selectedChats.length) {
      selectedChats.forEach((chatId) => {
        remove(ref(db, `chats/${chatId}`)).catch((error) => {
          console.error(`Error deleting chat ${chatId}:`, error);
        });
      });
      setSelectedChats([]);
      setMultiSelectMode(false);
      setShowMultiDeleteConfirm(false);
      if (selectedChats.includes(selectedChat)) {
        setSelectedChat(null);
        if (isMobileView) {
          setShowMobileChatView(false);
        }
      }
    }
  };
  // Optimized auto-scroll with debouncing
  useEffect(() => {
    if (messagesEndRef.current) {
      // Use a small delay to allow for smooth rendering
      const timeoutId = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [chatMessages.length, selectedChat]); // Only scroll when message count changes or chat changes

  useEffect(() => {
    return () => {
      if (auth.currentUser && isAuthenticated) {
        cleanupAdminPresence(auth.currentUser.uid);
      }
    };
  }, [isAuthenticated]);

  useEffect(() => {}, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <div className="w-full bg-linear-to-br from-pink-500 via-purple-600 to-indigo-600">
        <Navbar
          isAuthenticated={isAuthenticated}
          handleLogout={handleLogout}
          setShowProfileOverlay={setShowProfileOverlay}
        />
        <div
          className="flex items-center justify-center"
          style={{
            height: `${viewportHeight - 64}px`,
          }}
        >
          <div className="bg-gray-900/80 p-6 sm:p-10 rounded-2xl shadow-2xl w-[90%] max-w-md">
            <h2 className="text-xl sm:text-2xl font-extrabold mb-6 sm:mb-8 text-center text-white tracking-wide">
              Admin Login
            </h2>
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-300"
                >
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  className="mt-2 w-full px-4 py-3 bg-gray-800 text-white rounded-md border border-transparent focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your email"
                  value={credentials.email}
                  onChange={(e) =>
                    setCredentials({ ...credentials, email: e.target.value })
                  }
                />
              </div>
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-300"
                >
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  className="mt-2 w-full px-4 py-3 bg-gray-800 text-white rounded-md border border-transparent focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your password"
                  value={credentials.password}
                  onChange={(e) =>
                    setCredentials({ ...credentials, password: e.target.value })
                  }
                />
              </div>
              <button
                type="submit"
                className="w-full py-3 bg-linear-to-r from-blue-600 to-purple-600 rounded-md text-white font-bold hover:from-purple-600 hover:to-blue-600 transition transform hover:scale-105"
              >
                Sign In
              </button>
            </form>
          </div>
        </div>
        {showNameModal && renderNameModal()}
      </div>
    );
  }
  // Mobile view
  if (isMobileView) {
    return (
      <div className="w-full bg-linear-to-br from-gray-800 to-gray-900 text-gray-200 overflow-hidden">
        <Navbar
          isAuthenticated={isAuthenticated}
          handleLogout={handleLogout}
          setShowProfileOverlay={setShowProfileOverlay}
        />

        <div
          style={{
            height: `${viewportHeight - 64}px`,
          }}
        >
          {!showMobileChatView ? (
            <div className="h-full flex flex-col">
              <div
                className="p-4 border-b border-gray-700 flex justify-between items-center bg-gray-800/90"
                id="safePad"
              >
                <h2 className="text-xl font-bold tracking-wide text-white">
                  Chats
                </h2>
                <button
                  onClick={() => {
                    setMultiSelectMode((prev) => !prev);
                    if (multiSelectMode) setSelectedChats([]);
                  }}
                  className="p-2 rounded-md bg-gray-700 hover:bg-gray-600 transition-all duration-200 transform hover:scale-105"
                  title="Toggle multi-select mode"
                >
                  {multiSelectMode ? (
                    <FiX size={22} className="text-white" />
                  ) : (
                    <FiCheckSquare size={22} className="text-white" />
                  )}
                </button>
              </div>

              <div className="p-4 overflow-y-auto flex-grow" id="safePad">
                <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                  {["Active", "Connected", "Ended", "All"].map((category) => (
                    <button
                      key={category}
                      onClick={() => setChatFilter(category)}
                      className={`py-2 px-3 rounded-md transition-all duration-200 font-semibold whitespace-nowrap focus:outline-hidden ${
                        chatFilter === category
                          ? "bg-linear-to-r from-indigo-500 to-purple-500 text-white"
                          : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>

                {multiSelectMode && selectedChats.length > 0 && (
                  <div className="mb-4">
                    <button
                      onClick={deleteSelectedChats}
                      className="w-full py-2 bg-red-600 hover:bg-red-500 text-white rounded-md transition-all duration-200 transform hover:scale-105 font-semibold"
                    >
                      Delete Selected ({selectedChats.length})
                    </button>
                  </div>
                )}

                {filteredChats.length > 0 ? (
                  filteredChats.map(([chatId, chatData]) => {
                    const newCount =
                      chatData.messages && !chatData.connectedAdmin?.email
                        ? Object.values(chatData.messages).filter(
                            (msg) =>
                              msg.sender === "user" && msg.status !== "read"
                          ).length
                        : chatData.connectedAdmin?.email === adminEmail
                          ? Object.values(chatData.messages || {}).filter(
                              (msg) =>
                                msg.sender === "user" && msg.status !== "read"
                            ).length
                          : 0;
                    return (
                      <div
                        key={chatId}
                        onClick={() =>
                          multiSelectMode
                            ? toggleChatSelection(chatId)
                            : handleChatSelect(chatId)
                        }
                        className={`p-4 mb-4 cursor-pointer rounded-lg transition-all duration-200 shadow-md hover:shadow-xl transform hover:-translate-y-1 ${
                          !multiSelectMode && selectedChat === chatId
                            ? "bg-gray-700"
                            : "bg-gray-800"
                        } ${multiSelectMode && selectedChats.includes(chatId) ? "ring-2 ring-indigo-400" : ""} flex items-center`}
                      >
                        {multiSelectMode && (
                          <input
                            type="checkbox"
                            checked={selectedChats.includes(chatId)}
                            className="h-4 w-4 accent-indigo-500 mr-4 pointer-events-none"
                          />
                        )}
                        <div className="flex-grow">
                          <div className="flex justify-between items-center">
                            <span className="font-bold text-lg">
                              Chat {chatId.slice(-6)}
                            </span>
                            <div className="flex items-center gap-2">
                              {!multiSelectMode &&
                                newCount > 0 &&
                                !chatData.ended && (
                                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                                    {newCount}
                                  </span>
                                )}
                              {!multiSelectMode &&
                                (!chatData.connectedAdmin?.email ||
                                  chatData.connectedAdmin?.email ===
                                    adminEmail) && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteClick(chatId);
                                    }}
                                    className="text-gray-400 hover:text-gray-200 transition-all duration-200"
                                  >
                                    <FiTrash2 size={18} />
                                  </button>
                                )}
                            </div>
                          </div>
                          <div className="text-xs mt-1">
                            {(chatData.ended ||
                              chatData.status?.state === "ended") && (
                              <span className="text-red-400 font-semibold">
                                Ended
                              </span>
                            )}
                          </div>
                          {chatData.messages && (
                            <div className="text-xs text-gray-400 mt-1">
                              Last:{" "}
                              {new Date(
                                Object.values(chatData.messages).slice(
                                  -1
                                )[0].timestamp
                              ).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: false,
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-center text-gray-400">No chats found.</p>
                )}
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col">
              <div
                className="p-4 border-b border-gray-700 flex justify-between items-center bg-gray-800/90"
                id="safePad"
              >
                <div className="flex items-center">
                  <button
                    onClick={handleBackToList}
                    className="p-2 rounded-md bg-gray-700 hover:bg-gray-600 transition-all duration-200 mr-2"
                  >
                    <FiArrowLeft size={20} className="text-white" />
                  </button>
                  <h2 className="text-xl font-bold tracking-wide text-white">
                    {selectedChat
                      ? `Chat ${selectedChat.slice(-6)}`
                      : "Select a Chat"}
                  </h2>
                </div>

                {selectedChat && (
                  <div>
                    {selectedChatStatus !== "ended" ? (
                      chats[selectedChat]?.connectedAdmin ? (
                        chats[selectedChat]?.connectedAdmin?.email !==
                        adminEmail ? (
                          <span className="text-sm text-gray-400">
                            Connected by{" "}
                            {chats[selectedChat].connectedAdmin.name}
                          </span>
                        ) : (
                          <button
                            onClick={() => markChatEnded(selectedChat)}
                            className="py-2 px-4 bg-red-500 hover:bg-red-400 text-white rounded-md transition-all duration-200 transform hover:scale-105 font-semibold text-sm"
                          >
                            End Chat
                          </button>
                        )
                      ) : (
                        <button
                          onClick={handleConnectChat}
                          className="py-2 px-4 bg-green-500 hover:bg-green-400 text-white rounded-md transition-all duration-200 transform hover:scale-105 font-semibold text-sm"
                        >
                          Connect
                        </button>
                      )
                    ) : (
                      <span className="text-sm text-gray-400">Chat Ended</span>
                    )}
                  </div>
                )}
              </div>

              <div
                className="flex-grow p-4 overflow-y-auto bg-gray-800 scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-gray-800"
                id="safePad"
              >
                {" "}
                {selectedChat ? (
                  chatMessages.length > 0 ? (
                    chatMessages.map((msg) => (
                      <Message
                        key={msg.id}
                        msg={msg}
                        isLastAdminMsg={msg.id === lastAdminMsgId}
                        isLastReadAdminMsg={msg.id === lastReadAdminMsgId}
                        isMobileView={true}
                      />
                    ))
                  ) : (
                    <p className="text-center text-gray-400 text-base">
                      No messages yet.
                    </p>
                  )
                ) : (
                  <p className="text-center text-gray-400 text-xl">
                    Select a chat to begin...
                  </p>
                )}
                <div ref={messagesEndRef} />
                {selectedChat && userTyping && (
                  <div className="mb-4 flex justify-start">
                    <div className="px-4 py-3 rounded-2xl max-w-[75%] bg-gray-700 text-white shadow-md flex items-center">
                      <div className="flex space-x-1">
                        <div
                          className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                          style={{ animationDelay: "0ms" }}
                        />
                        <div
                          className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                          style={{ animationDelay: "150ms" }}
                        />
                        <div
                          className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                          style={{ animationDelay: "300ms" }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-gray-700 bg-gray-800/90 backdrop-blur-xs">
                {!selectedChat ? (
                  <div className="text-center text-gray-400 text-base">
                    Select a chat to start messaging.
                  </div>
                ) : selectedChatStatus === "ended" ? (
                  <div className="text-center text-gray-500 text-base">
                    This chat has ended. Conversation history is above.
                  </div>
                ) : !isChatConnected ? (
                  <div className="text-center text-gray-400 text-base">
                    You are not connected to this chat. Press
                    &quot;Connect&quot; above.
                  </div>
                ) : (
                  <>
                    <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
                      {quickResponses.map((qr, idx) => (
                        <button
                          key={idx}
                          onClick={() => setResponse(qr)}
                          className="whitespace-nowrap py-2 px-3 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-all duration-200 transform hover:scale-105 font-medium text-sm"
                        >
                          {qr.length > 20 ? qr.substring(0, 20) + "..." : qr}
                        </button>
                      ))}
                    </div>
                    <div className="flex">
                      <textarea
                        value={response}
                        onChange={(e) => {
                          setResponse(e.target.value);
                          handleTyping();
                          e.target.style.height = "auto";
                          e.target.style.height = `${e.target.scrollHeight}px`;
                        }}
                        onKeyDown={(e) => {
                          if (e.shiftKey && e.key === "Tab") {
                            e.preventDefault();
                            const { selectionStart, selectionEnd } = e.target;
                            const newValue =
                              response.substring(0, selectionStart) +
                              "\n" +
                              response.substring(selectionEnd);
                            setResponse(newValue);
                          } else if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSendResponse();
                          }
                        }}
                        disabled={!isChatConnected}
                        className="flex-grow px-4 py-3 bg-gray-900 border border-gray-700 rounded-l-lg focus:outline-hidden focus:ring-2 focus:ring-indigo-500 resize-none overflow-y-auto text-lg placeholder-gray-400"
                        placeholder="Type your response..."
                        rows={1}
                        style={{ minHeight: "50px", maxHeight: "100px" }}
                      />
                      <button
                        onClick={handleSendResponse}
                        disabled={!isChatConnected}
                        className="py-3 px-4 bg-linear-to-r from-indigo-500 to-blue-600 hover:from-blue-600 hover:to-indigo-500 text-white rounded-r-lg transition-all duration-200 transform hover:scale-105 disabled:opacity-50 font-bold"
                      >
                        Send
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {showNameModal && renderNameModal()}
          {showProfileOverlay && renderProfileOverlay()}
          {showDeleteConfirm && (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
              <div className="bg-gray-900/90 p-6 rounded-xl shadow-2xl transform transition-all w-[90%] sm:max-w-sm">
                <h3 className="text-xl font-bold text-white mb-6 text-center">
                  Confirm Delete
                </h3>
                <p className="text-gray-400 mb-6 text-center">
                  Are you sure you want to delete this chat?
                </p>
                <div className="flex justify-center gap-6">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="py-2 px-6 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-all duration-200 transform hover:scale-105 font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDeleteChat}
                    className="py-2 px-6 bg-red-600 hover:bg-red-500 text-white rounded-md transition-all duration-200 transform hover:scale-105 font-semibold"
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          )}
          {showMultiDeleteConfirm && (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
              <div className="bg-gray-900/90 p-6 rounded-xl shadow-2xl transform transition-all w-[90%] sm:max-w-sm">
                <h3 className="text-xl font-bold text-white mb-6 text-center">
                  Confirm Delete
                </h3>
                <p className="text-gray-400 mb-6 text-center">
                  Are you sure you want to delete these chats?
                </p>
                <div className="flex justify-center gap-6">
                  <button
                    onClick={() => setShowMultiDeleteConfirm(false)}
                    className="py-2 px-6 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-all duration-200 transform hover:scale-105 font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDeleteSelectedChats}
                    className="py-2 px-6 bg-red-600 hover:bg-red-500 text-white rounded-md transition-all duration-200 transform hover:scale-105 font-semibold"
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Desktop view
  return (
    <div className=" w-full bg-linear-to-br from-gray-800 to-gray-900 text-gray-200 overflow-hidden">
      <Navbar
        isAuthenticated={isAuthenticated}
        handleLogout={handleLogout}
        setShowProfileOverlay={setShowProfileOverlay}
      />
      <div className="flex" style={{ height: `${viewportHeight - 64}px` }}>
        {isChatListCollapsed ? (
          <div className="w-22 border-r border-gray-700 flex flex-col bg-gray-800 shadow-lg">
            <div className="flex items-center justify-between p-6">
              <button
                onClick={() => setIsChatListCollapsed(false)}
                className="p-2 rounded-md bg-gray-700 hover:bg-gray-600 transition-all duration-200 transform hover:scale-105"
                title="Expand Chat List"
              >
                <FiChevronRight size={22} className="text-white" />
              </button>
            </div>
          </div>
        ) : (
          <div className="w-1/4 border-r border-gray-700 flex flex-col bg-gray-800 shadow-lg">
            <div className="flex items-center justify-between p-6">
              <button
                onClick={() => setIsChatListCollapsed(true)}
                className="p-2 rounded-md bg-gray-700 hover:bg-gray-600 transition-all duration-200 transform hover:scale-105"
                title="Collapse Chat List"
              >
                <FiChevronLeft size={22} className="text-white" />
              </button>
              <h2 className="text-2xl font-bold tracking-wider ml-4">Chats</h2>
              <button
                onClick={() => {
                  setMultiSelectMode((prev) => !prev);
                  if (multiSelectMode) setSelectedChats([]);
                }}
                className="p-2 rounded-md bg-gray-700 hover:bg-gray-600 transition-all duration-200 transform hover:scale-105"
                title="Toggle multi-select mode"
              >
                {multiSelectMode ? (
                  <FiX size={22} className="text-white" />
                ) : (
                  <FiCheckSquare size={22} className="text-white" />
                )}
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-grow">
              <div className="flex gap-2 mb-6">
                {["Active", "Connected", "Ended", "All"].map((category) => (
                  <button
                    key={category}
                    onClick={() => setChatFilter(category)}
                    className={`flex-1 py-2 rounded-md transition-all duration-200 font-semibold focus:outline-hidden ${
                      chatFilter === category
                        ? "bg-linear-to-r from-indigo-500 to-purple-500 text-white"
                        : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
              {multiSelectMode && selectedChats.length > 0 && (
                <div className="mb-4">
                  <button
                    onClick={deleteSelectedChats}
                    className="w-full py-2 bg-red-600 hover:bg-red-500 text-white rounded-md transition-all duration-200 transform hover:scale-105 font-semibold"
                  >
                    Delete Selected ({selectedChats.length})
                  </button>
                </div>
              )}
              {filteredChats.length > 0 ? (
                filteredChats.map(([chatId, chatData]) => {
                  const newCount =
                    chatData.messages && !chatData.connectedAdmin?.email
                      ? Object.values(chatData.messages).filter(
                          (msg) =>
                            msg.sender === "user" && msg.status !== "read"
                        ).length
                      : chatData.connectedAdmin?.email === adminEmail
                        ? Object.values(chatData.messages || {}).filter(
                            (msg) =>
                              msg.sender === "user" && msg.status !== "read"
                          ).length
                        : 0;
                  return (
                    <div
                      key={chatId}
                      onClick={() =>
                        multiSelectMode
                          ? toggleChatSelection(chatId)
                          : handleChatSelect(chatId)
                      }
                      className={`p-4 mb-4 cursor-pointer rounded-lg transition-all duration-200 shadow-md hover:shadow-xl transform hover:-translate-y-1 ${
                        !multiSelectMode && selectedChat === chatId
                          ? "bg-gray-700"
                          : "bg-gray-800"
                      } ${multiSelectMode && selectedChats.includes(chatId) ? "ring-2 ring-indigo-400" : ""} flex items-center`}
                    >
                      {multiSelectMode && (
                        <input
                          type="checkbox"
                          checked={selectedChats.includes(chatId)}
                          className="h-4 w-4 accent-indigo-500 mr-4 pointer-events-none"
                        />
                      )}
                      <div className="flex-grow">
                        <div className="flex justify-between items-center">
                          <span className="font-bold text-lg">
                            Chat {chatId.slice(-6)}
                          </span>
                          <div className="flex items-center gap-2">
                            {!multiSelectMode &&
                              newCount > 0 &&
                              !chatData.ended && (
                                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                                  {newCount}
                                </span>
                              )}
                            {!multiSelectMode &&
                              (!chatData.connectedAdmin?.email ||
                                chatData.connectedAdmin?.email ===
                                  adminEmail) && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteClick(chatId);
                                  }}
                                  className="text-gray-400 hover:text-gray-200 transition-all duration-200"
                                >
                                  <FiTrash2 size={18} />
                                </button>
                              )}
                          </div>
                        </div>
                        <div className="text-xs mt-1">
                          {(chatData.ended ||
                            chatData.status?.state === "ended") && (
                            <span className="text-red-400 font-semibold">
                              Ended
                            </span>
                          )}
                        </div>
                        {chatData.messages && (
                          <div className="text-xs text-gray-400 mt-1">
                            Last:{" "}
                            {new Date(
                              Object.values(chatData.messages).slice(
                                -1
                              )[0].timestamp
                            ).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false,
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-center text-gray-400">No chats found.</p>
              )}
            </div>
          </div>
        )}

        <div
          className={`${isChatListCollapsed ? "flex-grow" : "w-3/4"} flex flex-col`}
        >
          <div className="p-6 border-b border-gray-700 flex justify-between items-center bg-gray-800/90">
            <h2 className="text-2xl font-bold tracking-wide text-white">
              {selectedChat
                ? `Chat ${selectedChat.slice(-6)}`
                : "Select a Chat"}
            </h2>
            <div className="flex items-center gap-6">
              {selectedChat && (
                <>
                  {selectedChatStatus !== "ended" ? (
                    chats[selectedChat]?.connectedAdmin ? (
                      chats[selectedChat]?.connectedAdmin?.email !==
                      adminEmail ? (
                        <span className="text-base text-gray-400">
                          Connected by {chats[selectedChat].connectedAdmin.name}
                        </span>
                      ) : (
                        <button
                          onClick={() => markChatEnded(selectedChat)}
                          className="py-2 px-4 bg-red-500 hover:bg-red-400 text-white rounded-md transition-all duration-200 transform hover:scale-105 font-semibold"
                        >
                          End Chat
                        </button>
                      )
                    ) : (
                      <button
                        onClick={handleConnectChat}
                        className="py-2 px-4 bg-green-500 hover:bg-green-400 text-white rounded-md transition-all duration-200 transform hover:scale-105 font-semibold"
                      >
                        Connect
                      </button>
                    )
                  ) : (
                    <span className="text-base text-gray-400">Chat Ended</span>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="flex-grow p-6 overflow-y-auto bg-gray-800 scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-gray-800">
            {" "}
            {selectedChat ? (
              chatMessages.length > 0 ? (
                chatMessages.map((msg) => (
                  <Message
                    key={msg.id}
                    msg={msg}
                    isLastAdminMsg={msg.id === lastAdminMsgId}
                    isLastReadAdminMsg={msg.id === lastReadAdminMsgId}
                    isMobileView={false}
                  />
                ))
              ) : (
                <p className="text-center text-gray-400 text-base">
                  No messages yet.
                </p>
              )
            ) : (
              <p className="text-center text-gray-400 text-xl">
                Select a chat to begin...
              </p>
            )}
            <div ref={messagesEndRef} />
            {selectedChat && userTyping && (
              <div className="mb-6 flex justify-start">
                <div className="px-4 py-3 rounded-2xl max-w-md bg-gray-700 text-white shadow-md flex items-center">
                  <div className="flex space-x-1">
                    <div
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "0ms" }}
                    />
                    <div
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "150ms" }}
                    />
                    <div
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "300ms" }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="p-6 border-t border-gray-700 bg-gray-800/90 backdrop-blur-xs">
            {!selectedChat ? (
              <div className="text-center text-gray-400 text-base">
                Select a chat to start messaging.
              </div>
            ) : selectedChatStatus === "ended" ? (
              <div className="text-center text-gray-500 text-base">
                This chat has ended. Conversation history is above.
              </div>
            ) : !isChatConnected ? (
              <div className="text-center text-gray-400 text-base">
                You are not connected to this chat. Press &quot;Connect&quot;
                above.
              </div>
            ) : (
              <>
                <div className="flex gap-3 mb-4 overflow-x-auto">
                  {quickResponses.map((qr, idx) => (
                    <button
                      key={idx}
                      onClick={() => setResponse(qr)}
                      className="whitespace-nowrap py-2 px-4 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-all duration-200 transform font-medium"
                    >
                      {qr}
                    </button>
                  ))}
                </div>
                <div className="flex">
                  <textarea
                    value={response}
                    onChange={(e) => {
                      setResponse(e.target.value);
                      handleTyping();
                      e.target.style.height = "auto";
                      e.target.style.height = `${e.target.scrollHeight}px`;
                    }}
                    onKeyDown={(e) => {
                      if (e.shiftKey && e.key === "Tab") {
                        e.preventDefault();
                        const { selectionStart, selectionEnd } = e.target;
                        const newValue =
                          response.substring(0, selectionStart) +
                          "\n" +
                          response.substring(selectionEnd);
                        setResponse(newValue);
                      } else if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSendResponse();
                      }
                    }}
                    disabled={!isChatConnected}
                    className="flex-grow px-4 py-3 bg-gray-900 border border-gray-700 rounded-l-lg focus:outline-hidden focus:ring-2 focus:ring-indigo-500 resize-none overflow-y-auto text-lg placeholder-gray-400"
                    placeholder="Type your response..."
                    rows={1}
                    style={{ minHeight: "60px", maxHeight: "120px" }}
                  />
                  <button
                    onClick={handleSendResponse}
                    disabled={!isChatConnected}
                    className="py-3 px-6 bg-linear-to-r from-indigo-500 to-blue-600 hover:from-blue-600 hover:to-indigo-500 text-white rounded-r-lg transition-all duration-200 transform hover:scale-105 disabled:opacity-50 font-bold"
                  >
                    Send
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        {showNameModal && renderNameModal()}
        {showProfileOverlay && renderProfileOverlay()}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className="bg-gray-900/90 p-8 rounded-xl shadow-2xl transform transition-all sm:max-w-sm sm:w-full">
              <h3 className="text-xl font-bold text-white mb-6 text-center">
                Confirm Delete
              </h3>
              <p className="text-gray-400 mb-6 text-center">
                Are you sure you want to delete this chat?
              </p>
              <div className="flex justify-center gap-6">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="py-2 px-6 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-all duration-200 transform hover:scale-105 font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteChat}
                  className="py-2 px-6 bg-red-600 hover:bg-red-500 text-white rounded-md transition-all duration-200 transform hover:scale-105 font-semibold"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
        {showMultiDeleteConfirm && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className="bg-gray-900/90 p-8 rounded-xl shadow-2xl transform transition-all sm:max-w-sm sm:w-full">
              <h3 className="text-xl font-bold text-white mb-6 text-center">
                Confirm Delete
              </h3>
              <p className="text-gray-400 mb-6 text-center">
                Are you sure you want to delete these chats?
              </p>
              <div className="flex justify-center gap-6">
                <button
                  onClick={() => setShowMultiDeleteConfirm(false)}
                  className="py-2 px-6 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-all duration-200 transform hover:scale-105 font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteSelectedChats}
                  className="py-2 px-6 bg-red-600 hover:bg-red-500 text-white rounded-md transition-all duration-200 transform hover:scale-105 font-semibold"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(AdminPanel);
